// packages/web/src/components/user-avatar.tsx
/**
 * UserAvatar — account-system entry point in AppHeader + CourseSidebar.
 *
 * Anonymous: renders the ghost-user icon; click opens AuthModal.
 * Authenticated: renders the seal avatar (initials); click opens a small
 * dropdown with logout / change-password (Phase 1 minimum; the full account
 * drawer lands in a later phase).
 *
 * Visual: spec 附录 A · 印章学术.
 * Spec: docs/architecture/account-system-design.md §0.1, §1.1
 */

import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/use-t.js';
import { useStore } from '../lib/store/index.js';
import { AuthModal, type AuthMode } from './account/auth-modal.js';
import { AccountDrawer } from './account/account-drawer.js';

export function UserAvatar() {
  const t = useT();
  const currentUser = useStore((s) => s.currentUser);
  const logout = useStore((s) => s.logout);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<AuthMode>('login');
  /** Reset-password token from ?reset-token= URL param (D3). null in normal use. */
  const [resetToken, setResetToken] = useState<string | null>(null);
  /** 19BO+ Phase B: signup-verify token from ?signup-token= URL param. null in normal use. */
  const [signupToken, setSignupToken] = useState<string | null>(null);
  /** 19BO+ CROSS-ORIGIN-AUTH: magic-link verify token from ?magic-token= URL param. null in normal use. */
  const [magicToken, setMagicToken] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 19BN Phase D D3: on mount, detect a ?reset-token= URL param (the reset
  // email link points here) and open the modal directly in resetPassword mode.
  // The token is consumed once; the reset form clears the URL param on success.
  // 19BO+ Phase B: same pattern for ?signup-token= → signupVerify mode.
  // 19BO+ CROSS-ORIGIN-AUTH: same pattern for ?magic-token= → magicVerify mode.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const reset = params.get('reset-token');
    const signup = params.get('signup-token');
    const magic = params.get('magic-token');
    if (reset) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate modal open state + token from ?reset-token=/?signup-token=/?magic-token= URL params on mount; one-shot consumption on the email-link landing entry
      setResetToken(reset);
      setModalMode('resetPassword');
      setModalOpen(true);
    } else if (signup) {
      setSignupToken(signup);
      setModalMode('signupVerify');
      setModalOpen(true);
    } else if (magic) {
      setMagicToken(magic);
      setModalMode('magicVerify');
      setModalOpen(true);
    }
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const isAuthed = currentUser && !currentUser.isAnonymous;
  const label = isAuthed ? t('auth.profile') : t('auth.login');

  function handleClick() {
    if (isAuthed) {
      setDropdownOpen((v) => !v);
    } else {
      setModalMode('login');
      setModalOpen(true);
    }
  }

  function handleLogout() {
    setDropdownOpen(false);
    void logout();
  }

  function handleChangePassword() {
    setDropdownOpen(false);
    setModalMode('password');
    setModalOpen(true);
  }

  const initials = isAuthed && currentUser?.displayName
    ? currentUser.displayName.slice(0, 1).toUpperCase()
    : '';

  return (
    <>
      <button
        type="button"
        className="user-avatar-btn"
        title={label}
        aria-label={label}
        aria-haspopup={isAuthed ? 'menu' : 'dialog'}
        aria-expanded={isAuthed ? dropdownOpen : modalOpen}
        onClick={handleClick}
      >
        {isAuthed && currentUser?.avatarUrl ? (
          <img src={currentUser.avatarUrl} alt="" className="user-avatar-img" width={20} height={20} />
        ) : isAuthed && initials ? (
          <span
            className="user-avatar-seal"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: 'var(--color-primary)',
              color: 'var(--color-white)',
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 11,
            }}
          >
            {initials}
          </span>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        )}
      </button>

      {isAuthed && dropdownOpen && (
        <div className="auth-avatar-dropdown" ref={dropdownRef} role="menu">
          <div className="auth-avatar-dropdown-head">
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-primary)', color: 'var(--color-white)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
              {initials}
            </div>
            <div>
              <div className="auth-avatar-dropdown-name">{currentUser?.displayName}</div>
              {currentUser?.email && <div className="auth-avatar-dropdown-email">{currentUser.email}</div>}
            </div>
          </div>
          <button type="button" className="auth-avatar-dropdown-item" onClick={() => { setDropdownOpen(false); setDrawerOpen(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <circle cx="8" cy="8" r="6" /><path d="M18.09 10.37A6 6 0 1 1 10.34 18" /><path d="M7 6h1v4" />
            </svg>
            {t('auth.profile')}
          </button>
          <button type="button" className="auth-avatar-dropdown-item" onClick={handleChangePassword}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <rect width="18" height="11" x="3" y="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {t('auth.changePassword')}
          </button>
          <div className="auth-avatar-dropdown-sep" />
          <button type="button" className="auth-avatar-dropdown-item danger" onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('auth.logout')}
          </button>
        </div>
      )}

      <AuthModal
        open={modalOpen}
        initialMode={modalMode}
        resetToken={resetToken ?? undefined}
        signupToken={signupToken ?? undefined}
        magicToken={magicToken ?? undefined}
        onClose={() => setModalOpen(false)}
      />
      <AccountDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
